import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { X, Plus, Edit2, Trash2, UserCheck, Upload, Link } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DoctorSettings = ({ token, onClose }) => {
  const [doctors, setDoctors] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState('');
  const [qualification, setQualification] = useState('');
  const [info, setInfo] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [photoMode, setPhotoMode] = useState('url'); // 'url' or 'upload'
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchDoctors = async () => {
    try {
      const response = await axios.get(`${API}/doctors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDoctors(response.data);
    } catch (error) {
      console.error('Error fetching doctors:', error);
    }
  };

  useEffect(() => {
    fetchDoctors();
  }, []);

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, GIF, or WebP');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/doctor-photo`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setPhotoUrl(`${BACKEND_URL}${response.data.url}`);
      toast.success('Photo uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim() || !qualification.trim()) {
      toast.error('Please fill name and qualification');
      return;
    }

    setLoading(true);
    try {
      if (editingId) {
        // Update existing doctor
        await axios.put(
          `${API}/doctors/${editingId}`,
          { name, qualification, info: info || null, photo_url: photoUrl || null },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Doctor updated successfully');
      } else {
        // Create new doctor
        await axios.post(
          `${API}/doctors`,
          { name, qualification, info: info || null, photo_url: photoUrl || null },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Doctor added successfully');
      }
      
      setName('');
      setQualification('');
      setInfo('');
      setPhotoUrl('');
      setPhotoMode('url');
      setEditingId(null);
      fetchDoctors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (doctor) => {
    setEditingId(doctor.id);
    setName(doctor.name);
    setQualification(doctor.qualification);
    setInfo(doctor.info || '');
    setPhotoUrl(doctor.photo_url || '');
  };

  const handleCancel = () => {
    setEditingId(null);
    setName('');
    setQualification('');
    setInfo('');
    setPhotoUrl('');
    setPhotoMode('url');
  };

  const handleDelete = async (doctorId) => {
    if (!confirm('Are you sure you want to delete this doctor?')) return;

    try {
      await axios.delete(`${API}/doctors/${doctorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Doctor deleted successfully');
      fetchDoctors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const handleSetOnDuty = async (doctorId) => {
    try {
      await axios.patch(`${API}/doctors/${doctorId}/set-on-duty`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Doctor set as on duty');
      fetchDoctors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set on duty');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <Card className="p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Manage Doctors
          </h2>
          <Button
            onClick={onClose}
            variant="ghost"
            size="icon"
            data-testid="close-settings-button"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Add/Edit Form */}
        <div className="mb-6 p-4 bg-slate-50 rounded-lg">
          <h3 className="text-lg font-semibold mb-4">
            {editingId ? 'Edit Doctor' : 'Add New Doctor'}
          </h3>
          <div className="space-y-4">
            <div>
              <Label htmlFor="doctor-name">Doctor Name</Label>
              <Input
                id="doctor-name"
                data-testid="doctor-name-input"
                type="text"
                placeholder="e.g., DR. NAZAR"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="doctor-qualification">Qualification</Label>
              <Input
                id="doctor-qualification"
                data-testid="doctor-qualification-input"
                type="text"
                placeholder="e.g., MD(GEN. MED.)"
                value={qualification}
                onChange={(e) => setQualification(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="doctor-info">Additional Info (shown on TV)</Label>
              <Input
                id="doctor-info"
                data-testid="doctor-info-input"
                type="text"
                placeholder="e.g., Specialist in ENT, 15 years experience"
                value={info}
                onChange={(e) => setInfo(e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                This will be displayed below the qualification on TV
              </p>
            </div>
            <div>
              <Label>Doctor Photo (for TV Display)</Label>
              {/* Photo Mode Toggle */}
              <div className="flex gap-2 mt-2 mb-3">
                <Button
                  type="button"
                  variant={photoMode === 'upload' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPhotoMode('upload')}
                  className="flex-1"
                >
                  <Upload className="h-4 w-4 mr-1" />
                  Upload File
                </Button>
                <Button
                  type="button"
                  variant={photoMode === 'url' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPhotoMode('url')}
                  className="flex-1"
                >
                  <Link className="h-4 w-4 mr-1" />
                  Use URL
                </Button>
              </div>
              
              {photoMode === 'upload' ? (
                <div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="w-full"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {uploading ? 'Uploading...' : 'Choose Photo'}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-1">
                    Max 5MB. Formats: JPEG, PNG, GIF, WebP
                  </p>
                </div>
              ) : (
                <div>
                  <Input
                    id="doctor-photo"
                    data-testid="doctor-photo-input"
                    type="url"
                    placeholder="https://example.com/doctor-photo.jpg"
                    value={photoUrl}
                    onChange={(e) => setPhotoUrl(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Enter URL to an online photo
                  </p>
                </div>
              )}
              
              {/* Photo Preview */}
              {photoUrl && (
                <div className="mt-3 flex items-center gap-3">
                  <img 
                    src={photoUrl} 
                    alt="Preview" 
                    className="w-20 h-20 rounded-full object-cover border-2 border-green-500"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setPhotoUrl('')}
                    className="text-red-600 hover:text-red-700"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Remove
                  </Button>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleSave}
                data-testid="save-doctor-button"
                disabled={loading || uploading}
                className="flex-1"
              >
                {loading ? 'Saving...' : editingId ? 'Update' : 'Add Doctor'}
              </Button>
              {editingId && (
                <Button
                  onClick={handleCancel}
                  variant="outline"
                  data-testid="cancel-edit-button"
                >
                  Cancel
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Doctors List */}
        <div>
          <h3 className="text-lg font-semibold mb-3">Current Doctors</h3>
          {doctors.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">No doctors added yet</p>
          ) : (
            <div className="space-y-2">
              {doctors.map((doctor) => (
                <div
                  key={doctor.id}
                  data-testid={`doctor-item-${doctor.id}`}
                  className={`flex items-center justify-between p-3 border rounded-lg ${
                    doctor.on_duty 
                      ? 'bg-green-50 border-green-300' 
                      : 'bg-white border-border'
                  }`}
                >
                  <div className="flex items-center gap-3 flex-1">
                    {doctor.photo_url ? (
                      <img 
                        src={doctor.photo_url} 
                        alt={doctor.name}
                        className="w-12 h-12 rounded-full object-cover border-2 border-slate-200"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center text-2xl">
                        👨‍⚕️
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <div className="font-semibold">{doctor.name}</div>
                        {doctor.on_duty && (
                          <span className="px-2 py-0.5 bg-green-600 text-white text-xs font-bold rounded-full">
                            ON DUTY
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">{doctor.qualification}</div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {!doctor.on_duty && (
                      <Button
                        onClick={() => handleSetOnDuty(doctor.id)}
                        variant="ghost"
                        size="sm"
                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                        data-testid={`set-on-duty-${doctor.id}`}
                      >
                        <UserCheck className="h-4 w-4 mr-1" />
                        Set On Duty
                      </Button>
                    )}
                    <Button
                      onClick={() => handleEdit(doctor)}
                      variant="ghost"
                      size="sm"
                      data-testid={`edit-doctor-${doctor.id}`}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      onClick={() => handleDelete(doctor.id)}
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-doctor-${doctor.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default DoctorSettings;
